<template lang="pug">
div
  CommunityQPublishModal
  StoryEmailModal

  .btn-group.pull-right
    router-link.btn.btn-info(to="/issues")
      i.fa.fa-arrow-left
      |
      | Back
    router-link.btn.btn-default(:to="{ name: 'cloneIssue', params: { issueId } }")
      i.fa.fa-copy
      |
      | Clone

  h1
    | {{ issue.title }}
    |
    small {{ issue.publication.name }}

  hr

  LoaderOverlay(:loading="loading || downloadingStories")
    .row
      .col-md-9
        h2 Pages

        IssuePagesIndex(:issue-id="issueId")

        hr

        h2 Stories

        StoryFileDrop
          .alert.alert-info(v-if="stories.length === 0") No stories were found
          StoriesTable(v-else :columns="COLUMNS" @sort="sortStories" @should-refresh="fetchStories()")

          div.text-right
            NewStoryButton
            |
            |
            button.btn.btn-default(@click="downloadStoriesZip" :disabled="downloadingStories")
              i.fa.fa-download(v-if="!downloadingStories")
              i.fa.fa-spinner.fa-spin(v-else)
              |
              | {{ downloadingStories ? 'Preparing Download...' : 'Download All Stories' }}

        hr

        .header
          h2 Files

          FileBreadcrumbs(:resource-slug="issue.slug")

          .download-all.text-right
            a.btn.btn-default(@click="downloadFilesZip")
              i.fa.fa-download
              |
              | Download All Files

        FileIndex(:resourceGlobalId="issueGlobalId")

      Sidebar.col-md-3.issue
</template>

<script>
import { mapGetters } from 'vuex'
import { sync } from 'vuex-pathify'
import { mapStores, mapState } from 'pinia'
import { useIssueStore } from '@/stores/issue'
import { useFilesStore } from '@/stores/files'
import { GlobalId } from '@/utils/global_id'
import httpClient from '@/api/httpClient'
import { trimEnd } from 'lodash'

import CommunityQPublishModal from '@/components/community_q/publish_modal.vue'
import FileBreadcrumbs from '@/components/files/breadcrumbs.vue'
import FileIndex from '@/components/files/index.vue'
import LoaderOverlay from '@/components/shared/loader_overlay.vue'
import IssuePagesIndex from '@/components/issues/pages/index.vue'
import NewStoryButton from '@/components/stories/index/new_story_button.vue'
import Sidebar from './sidebar.vue'
import StoriesTable from '@/components/stories/index/stories_table.vue'
import StoryEmailModal from '@/components/stories/email_modal.vue'
import StoryFileDrop from '@/components/stories/index/story_file_drop.vue'

const COLUMNS = [
  'color',
  'id',
  'byline',
  'slug',
  'sections',
  'words',
  'media',
  'exported',
  'squeue',
  'actions'
];

export default {
  components: {
    CommunityQPublishModal,
    FileBreadcrumbs,
    FileIndex,
    LoaderOverlay,
    IssuePagesIndex,
    NewStoryButton,
    Sidebar,
    StoriesTable,
    StoryEmailModal,
    StoryFileDrop
  },

  props: {
    issueId: {
      type: Number,
      required: true
    }
  },

  data() {
    return {
      loading: true,
      downloadingStories: false
    }
  },

  computed: {
    ...mapStores(useIssueStore),
    ...mapState(useIssueStore, ['issue']),
    stories: sync('stories/stories'),
    ...mapStores(useFilesStore),
    ...mapGetters('stories', { storyIds: 'ids' }),

    issueGlobalId() {
      return new GlobalId({ model: 'Issue', id: this.issueId }).toString()
    },
  },

  async created() {
    this.COLUMNS = COLUMNS
  },

  async mounted() {
    const { issueId } = this;

    this.issueStore.$reset()
    await this.issueStore.fetch({ issueId })
    await this.$store.dispatch('squeues/fetchOnce')
    await this.fetchStories()

    this.loading = false
  },

  methods: {
    fetchStories(queryAddons = {}) {
      // If it's in one of the pages within this issue. We don't want to show it here,
      // we show it on it's page within the issue instead.
      const exclude_page_ids = this.issue.pages.map(page => page.id)

      return this.$store.dispatch(
        'stories/fetch',
        {
          query: {
            issues_id_eq: this.issueId,
            exclude_page_ids,
            ...queryAddons
          },
          // We don't want to really do pagination at this level
          page: 1, prePage: 1000
        }
      )
    },

    sortStories({ column, direction }) {
      this.fetchStories({ s: `${column} ${direction}` })
    },
    parameterize(str) {
      // Mimic Rails' parameterize: lowercase, replace non-alphanumeric with hyphens, remove duplicates
      return str
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '') // trim leading/trailing hyphens
    },

    buildStoriesZipFilename(stories, storyIds) {
      // Build filename using same logic as Ruby Zipper::Stories
      const parts = ['newsroomq-download']
      parts.push(stories.length === 1 ? 'story' : 'stories')
      
      if (stories.length === 1) {
        // Single story: use slug for friendlier name
        const slug = this.parameterize(stories[0].slug)
        parts.push(slug)
      } else {
        // Multiple stories: use first 3 IDs
        const idsToShow = storyIds.slice(0, 3)
        parts.push(...idsToShow)
        if (stories.length > 3) {
          parts.push('and-more')
        }
      }
      
      return `${parts.join('-')}.zip`
    },

    async downloadStoriesZip() {
      try {
        const { data: { stories } } = await httpClient.get(`/api/internal/stories.json?q[issues_id_eq]=${this.issueId}`)
        const storyIds = stories.map(story => story.id)

        if (!storyIds || storyIds.length === 0) {
          this.$store.dispatch('messages/addAlert', 'No stories available to download')
          return
        }

        this.downloadingStories = true

        const filename = this.buildStoriesZipFilename(stories, storyIds)

        // Fetch the zip file as a blob
        const response = await httpClient.get(`/stories.zip?${jQuery.param({ ids: storyIds })}`, {
          responseType: 'blob'
        })

        // Create a blob URL and trigger download
        const blob = new Blob([response.data], { type: 'application/zip' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = filename
        link.click()
        
        // Cleanup
        window.URL.revokeObjectURL(url)

        this.$store.dispatch('messages/addNotice', 'Stories downloaded successfully')
      } catch (error) {
        console.error('Error downloading stories:', error)
        this.$store.dispatch('messages/addAlert', 'Failed to download stories. Please try again.')
      } finally {
        this.downloadingStories = false
      }
    },

    downloadFilesZip() {
      const path = this.filesStore.resourceFilesPath()

      window.location = `${trimEnd(path, '/')}.zip`
    }
  }
}
</script>

<style scoped>
  .header {
    display: flex;
  }

  .header h2 {
    margin-right: 0.5em;
  }

  .header .download-all {
    flex-grow: 1;
  }
</style>
